import { z } from 'zod';

/**
 * Configuração da aplicação, validada no boot.
 *
 * F1.2 — REGRA INEGOCIÁVEL: toda variável com prefixo `VITE_` é compilada no
 * pacote enviado ao navegador e é PÚBLICA por construção. Chave secreta de
 * provedor de pagamento, `service_role` do banco ou token de administração
 * JAMAIS recebem esse prefixo — vivem apenas em `supabase secrets`, do lado
 * do servidor (ver D-02 e D-04 do plano).
 *
 * Antes desta camada, a URL do backend estava literal em `pages/Contato.tsx`
 * e `pages/ViverMais.tsx`, sem separação entre produção e homologação.
 */
const schema = z.object({
  /** URL do projeto Supabase. Ex.: https://xxxx.supabase.co */
  VITE_SUPABASE_URL: z.string().url('VITE_SUPABASE_URL precisa ser uma URL válida'),

  /**
   * Chave anônima. É pública — só enxerga catálogo ativo, por política de
   * acesso por linha. Sem acesso a pedidos, pagamentos ou dados de paciente.
   */
  VITE_SUPABASE_ANON_KEY: z.string().min(20, 'VITE_SUPABASE_ANON_KEY ausente ou curta'),

  /** Origem canônica do site, usada em URLs absolutas, SEO e retorno do checkout. */
  VITE_SITE_URL: z.string().url().default('https://labsantaizabel.com.br'),

  /** Telefone de WhatsApp em formato internacional, sem símbolos. */
  VITE_WHATSAPP: z.string().regex(/^\d{12,13}$/).default('5524988250515'),

  /** Liga o catálogo semente local em vez do snapshot publicado. Só desenvolvimento. */
  VITE_USE_SEED_CATALOG: z
    .string()
    .optional()
    .transform((v) => v === '1' || v === 'true'),

  /** DSN do monitoramento de erros. Vazio desliga o monitoramento. */
  VITE_SENTRY_DSN: z.string().optional().default(''),

  /** Identificador de medição de audiência. Vazio desliga a medição. */
  VITE_ANALYTICS_ID: z.string().optional().default(''),

  /**
   * Chat do site (Centriu Link). Identificador da organização.
   *
   * É público por construção, como a chave anônima acima: o script do widget
   * é lido por qualquer visitante. Quem autoriza de verdade é o servidor do
   * Centriu, cruzando `widget_token` com a lista de domínios liberados —
   * por isso o par abaixo não vale nada fora de labsantaizabel.com.br.
   *
   * Vazio desliga o chat. É o que se usa em homologação, para conversa de
   * teste não cair na fila de atendimento real.
   */
  VITE_CENTRIU_ORG_ID: z
    .string()
    .optional()
    .default('3adacd13-f386-45f1-b4d9-80db79629158'),

  /** Token do canal "Chat do Site" no painel do Centriu. Vazio desliga o chat. */
  VITE_CENTRIU_WIDGET_TOKEN: z
    .string()
    .optional()
    .default('283f010d-d693-45a4-9bbd-f730abc995c2'),
});

export type Env = z.infer<typeof schema>;

function load(): Env {
  const parsed = schema.safeParse(import.meta.env);

  if (!parsed.success) {
    const detalhe = parsed.error.issues
      .map((i) => `  · ${i.path.join('.')}: ${i.message}`)
      .join('\n');

    // Falhar no boot é deliberado: é melhor o site não subir do que subir
    // apontando para o projeto errado e gravar pedidos no lugar errado.
    throw new Error(
      `Configuração de ambiente inválida.\n${detalhe}\n\n` +
        `Copie .env.example para .env.local e preencha os valores.`,
    );
  }

  return parsed.data;
}

export const env = load();

/** Endpoint de uma Edge Function do projeto configurado. */
export function fn(name: string): string {
  return `${env.VITE_SUPABASE_URL}/functions/v1/${name}`;
}
